/* variant-media-filter.js
   Horizon: Show only the selected color's image "block" (featured image + following images)
   Works by:
   - Reading product JSON (variants + featured_media)
   - Finding the selected variant's featured media in the gallery
   - Showing media items from that point until the next color's featured media
*/

(function () {
  function getProductJson() {
    // Try common Shopify theme patterns
    const candidates = [
      document.querySelector('script[type="application/json"][data-product-json]'),
      document.querySelector('script[type="application/json"]#ProductJson'),
      document.querySelector('script[type="application/json"][id*="ProductJson"]'),
      document.querySelector('script[type="application/json"][id*="product-json"]')
    ].filter(Boolean);

    for (const el of candidates) {
      try {
        const json = JSON.parse(el.textContent);
        if (json && json.variants && json.variants.length) return json;
      } catch (_) {}
    }
    return null;
  }

  function getSelectedVariantIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('variant');
    return v ? Number(v) : null;
  }

  function findSelectedVariant(product) {
    const urlVariantId = getSelectedVariantIdFromUrl();
    if (urlVariantId) {
      const v = product.variants.find(x => Number(x.id) === urlVariantId);
      if (v) return v;
    }
    // Fallback: first available or first variant
    return product.variants.find(v => v.available) || product.variants[0];
  }

  function getColorOptionIndex(product) {
    // Find option position for "Color" (case-insensitive)
    const opts = product.options || [];
    for (let i = 0; i < opts.length; i++) {
      if (String(opts[i]).toLowerCase() === 'color') return i;
    }
    // If the theme doesn't expose product.options, default to option1 as color
    return 0;
  }

  function buildColorFeaturedMediaMap(product) {
    const colorIndex = getColorOptionIndex(product);
    const map = new Map(); // color -> featured_media_id
    for (const v of product.variants) {
      const color = (v.options && v.options[colorIndex]) || v.option1;
      const mediaId = v.featured_media && v.featured_media.id;
      if (color && mediaId && !map.has(color)) map.set(color, Number(mediaId));
    }
    return map;
  }

  function getGalleryRoot() {
    // Horizon/Dawn-like patterns
    return (
      document.querySelector('[data-media-gallery]') ||
      document.querySelector('media-gallery') ||
      document.querySelector('[id*="MediaGallery"]') ||
      document.querySelector('.product__media') ||
      document.querySelector('.product-media') ||
      document.querySelector('.product__media-wrapper')
    );
  }

  function getMediaItems(galleryRoot) {
    // Try to capture both main media + thumbnails
    const main = Array.from(
      galleryRoot.querySelectorAll('[data-media-id], [id*="MediaGallery"] [id*="Slide"], .product__media-item')
    );

    // Filter to unique elements that represent slides/media items
    const uniq = [];
    const seen = new Set();
    for (const el of main) {
      const key = el.getAttribute('data-media-id') || el.id || el;
      if (!seen.has(key)) {
        seen.add(key);
        uniq.push(el);
      }
    }
    return uniq;
  }

  function getMediaId(el) {
    const dm = el.getAttribute('data-media-id');
    if (dm) {
      // sometimes format is "sectionId-mediaId"
      const parts = dm.split('-');
      const last = parts[parts.length - 1];
      const asNum = Number(last);
      return Number.isFinite(asNum) ? asNum : null;
    }
    // Try extracting a number from id
    if (el.id) {
      const m = el.id.match(/(\d{6,})/);
      if (m) return Number(m[1]);
    }
    return null;
  }

  function setVisible(el, visible) {
    // Use hidden + display to cover different CSS rules
    el.hidden = !visible;
    el.style.display = visible ? '' : 'none';
  }

  function filterToColorBlock(product, selectedVariant) {
    const galleryRoot = getGalleryRoot();
    if (!galleryRoot) return;

    const items = getMediaItems(galleryRoot);
    if (!items.length) return;

    const colorMap = buildColorFeaturedMediaMap(product);
    const colorIndex = getColorOptionIndex(product);
    const selectedColor =
      (selectedVariant.options && selectedVariant.options[colorIndex]) ||
      selectedVariant.option1;

    const startMediaId = selectedVariant.featured_media && Number(selectedVariant.featured_media.id);
    if (!startMediaId) return;

    // Find the start index in the gallery based on featured media id
    const mediaIds = items.map(getMediaId);
    const startIndex = mediaIds.findIndex(id => id === startMediaId);

    if (startIndex === -1) return;

    // Determine next block boundary:
    // Find the next media item that is the featured media of ANY OTHER color.
    const featuredIdsByColor = new Set(Array.from(colorMap.values()).map(Number));
    const selectedFeaturedId = colorMap.get(selectedColor);

    let endIndex = items.length; // default to end
    for (let i = startIndex + 1; i < items.length; i++) {
      const id = mediaIds[i];
      if (!id) continue;
      if (featuredIdsByColor.has(id) && id !== Number(selectedFeaturedId)) {
        endIndex = i;
        break;
      }
    }

    // Show only [startIndex, endIndex)
    items.forEach((el, idx) => {
      setVisible(el, idx >= startIndex && idx < endIndex);
    });

    // Also handle thumbnails if they exist separately (common in Horizon)
    const thumbRoots = document.querySelectorAll(
      '[class*="thumbnail"], [id*="Thumbnail"], .product__media-thumbnail-list, .thumbnail-list'
    );
    thumbRoots.forEach(root => {
      const thumbs = Array.from(root.querySelectorAll('[data-media-id], a, button, li')).filter(Boolean);
      if (!thumbs.length) return;
      thumbs.forEach(t => {
        const id = getMediaId(t) || getMediaId(t.closest('[data-media-id]')) || null;
        if (!id) return; // leave visible if we can't detect
        const visible = mediaIds.indexOf(id) >= startIndex && mediaIds.indexOf(id) < endIndex;
        setVisible(t, visible);
      });
    });
  }

  function getCurrentVariant(product) {
    // Try getting variant from URL
    const fromUrl = getSelectedVariantIdFromUrl();
    if (fromUrl) {
      const v = product.variants.find(x => Number(x.id) === fromUrl);
      if (v) return v;
    }
    return findSelectedVariant(product);
  }

  function attachListeners(product) {
    // 1) Listen to URL changes (some themes update ?variant=)
    window.addEventListener('popstate', () => {
      const v = getCurrentVariant(product);
      if (v) filterToColorBlock(product, v);
    });

    // 2) Listen to change events from selects/radios (variant pickers)
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!target) return;

      // Common patterns: name="options[Color]" or name="option1"/"option2"
      const n = target.getAttribute('name') || '';
      const isVariantInput =
        n.includes('options') ||
        n === 'option1' ||
        n === 'option2' ||
        n === 'option3' ||
        target.closest('variant-selects') ||
        target.closest('variant-radios');

      if (!isVariantInput) return;

      // Delay a tick so theme finishes updating URL/variant
      setTimeout(() => {
        const v = getCurrentVariant(product);
        if (v) filterToColorBlock(product, v);
      }, 50);
    });

    // 3) Also catch custom variant events if theme dispatches them
    document.addEventListener('variant:change', () => {
      const v = getCurrentVariant(product);
      if (v) filterToColorBlock(product, v);
    });
  }

  function init() {
    const product = getProductJson();
    if (!product) return;

    const firstVariant = getCurrentVariant(product);
    if (firstVariant) filterToColorBlock(product, firstVariant);

    attachListeners(product);
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();